import { useMutation, useQuery } from "@tanstack/react-query";
import ProjectUserRepository from "./usersOfProject.repository";
import { transferProjectOwner } from "./ownerMigration.repository";
import { ProjectId, Role, UserStatus } from "./usersOfProject.modal";
import { selectVisibleUsers } from "./usersOfProject.selectors";
import { queryClient } from "../../../../../src/pages/_app";

export type patchUsersOfProjectProps = {
  projectId: ProjectId;
  patchData: { id: number; role: Role }[];
};

export interface getUsersOfProjectProps {
  projectId: ProjectId;
}

export interface createUserOfProjectProps {
  projectId: ProjectId;
  email: string;
}

export interface deleteUserOfProjectProps {
  projectId: ProjectId;
  email: string;
  status: UserStatus;
}

export interface transferProjectOwnerProps {
  projectId: number;
  newOwnerEmail: string;
}

/**
 * project-user write mutation 표준 재시도 정책(SoT): create/patch/delete 세 mutation 이 공유한다.
 * 정책이 바뀌면 세 mutation 이 한 곳에서 함께 바뀌도록 정의한다(엔드포인트별 백오프 불일치 방지).
 */
const PROJECT_USER_MUTATION_RETRY = { retry: 3, retryDelay: 1000 } as const;

/**
 * 프로젝트 멤버 목록 쿼리 키 — 조회(`useGetUsersOfProject`)와 멤버 추가·권한 변경·삭제·소유권 이전 뒤 무효화·재조회가
 * 모두 이 빌더로 키를 만든다.
 *
 * id 는 조회 경로(`projects/${projectId}/users`)에 들어가는 것과 같은 문자열로 맞춘다. `ProjectId` 는 숫자도 받는데
 * 키 원소는 타입까지 비교되므로(`42` ≠ `"42"`), 맞추지 않으면 숫자 id 로 만든 키가 문자열 id 로 선 쿼리에 닿지 않아
 * 무효화가 소리 없이 빗나간다. 문자열은 그대로 남으므로 멤버 관리가 고른 문자열 id(`selectedProject`)로 만든 키는
 * 정규화 전과 같다(`["getUsersOfProject", "42"]`).
 */
export const usersOfProjectQueryKey = (projectId: ProjectId) =>
  ["getUsersOfProject", String(projectId)] as const;

export const useGetUsersOfProject = ({ projectId }: getUsersOfProjectProps) => {
  return useQuery({
    queryKey: usersOfProjectQueryKey(projectId),
    queryFn: () => ProjectUserRepository.getUsersOfProject({ projectId }),
    enabled: !!projectId,
    select: selectVisibleUsers,
  });
};

export const useCreateUserOfProject = ({
  projectId,
}: {
  projectId: ProjectId;
}) => {
  return useMutation({
    mutationKey: ["createUserOfProject"],
    mutationFn: ({ projectId, email }: createUserOfProjectProps) => {
      return ProjectUserRepository.createUserOfProject({ projectId, email });
    },
    ...PROJECT_USER_MUTATION_RETRY,
  });
};

queryClient.refetchQueries([]);

export const usePatchUsersOfProject = () => {
  return useMutation({
    mutationKey: ["patchUsersOfProject"],
    mutationFn: ({ projectId, patchData }: patchUsersOfProjectProps) => {
      return ProjectUserRepository.patchUsersOfProject({
        projectId,
        patchData,
      });
    },
    ...PROJECT_USER_MUTATION_RETRY,
  });
};

export const useDeleteUserOfProject = () => {
  return useMutation({
    mutationKey: ["deleteUserOfProject"],
    mutationFn: ({ projectId, email, status }: deleteUserOfProjectProps) =>
      ProjectUserRepository.deleteUserOfProject({
        projectId,
        email,
        status,
      }),
    ...PROJECT_USER_MUTATION_RETRY,
  });
};

/**
 * 소유권 이관 — 멤버 CRUD 의 표준 재시도 정책을 **쓰지 않는다**. 이관은 되풀이하면 두 번째 호출이
 * 404(더는 주인이 아님)로 끝나, 타임아웃 뒤 자동 재시도가 실제로 성공한 이전을 실패로 보이게 한다.
 */
export const useTransferProjectOwner = () => {
  return useMutation({
    mutationKey: ["transferProjectOwner"],
    mutationFn: ({ projectId, newOwnerEmail }: transferProjectOwnerProps) =>
      transferProjectOwner(projectId, newOwnerEmail),
    retry: false,
  });
};
